import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { AppStack, AppText } from '../../app/common/ui'
import { Box, Container, Stepper, Group, Loader, Center } from '@mantine/core'
import { AppNumberInput, AppSelect } from '../../app/common/ui'
import { AppButton, AppCard, AppInput, StandardInput, standardInputFlexGroupSx } from '@gunslop/ui'
import { useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { CartridgeSpecFormValues, AmmoProductFormValues, CartridgeSpecDto } from '../../app/models/ammo'
import { PagedResult } from '../../app/models/pagedResult'
import { toast } from 'react-toastify'
import LabelWithHelp from '../help/LabelWithHelp'
import { AmmoHelpFieldKey } from '../help/ammoHelpContent'
import {
  AMMO_SUBMIT_RETURN_PATH,
  buildAmmoHelpUrl,
  consumeAmmoSubmitFormState,
  saveAmmoSubmitFormState,
  AmmoSubmitStep
} from '../help/ammoSubmitFormState'
import { AMMO_TYPES, BULLET_TYPES } from './ammoSubmitConstants'
import { applyAmmoProductFill } from './applyAmmoProductFill'
import {
  getAmmoAiFillErrorMessage,
  AI_FILL_FAILED_MESSAGE
} from './ammoAiFillErrors'
import { isAmmoProductFillResultValid } from './isAmmoProductFillResultValid'
import AiFillStatusAnnouncer from './AiFillStatusAnnouncer'
import CartridgePreviewCard from './CartridgePreviewCard'

const defaultSpecForm = (): CartridgeSpecFormValues => ({
  name: '',
  shortName: '',
  caliber: '',
  type: AMMO_TYPES[0]
})

const defaultAmmoForm = (): AmmoProductFormValues => ({
  cartridgeSpecId: '',
  brand: '',
  name: '',
  bulletType: BULLET_TYPES[0]
})

const SubmitAmmoPage = () => {
  const history = useHistory()
  const [activeStep, setActiveStep] = useState<AmmoSubmitStep>(0)

  const [specForm, setSpecForm] = useState<CartridgeSpecFormValues>(defaultSpecForm)
  const [ammoForm, setAmmoForm] = useState<AmmoProductFormValues>(defaultAmmoForm)
  const [matchedCartridgeSpecId, setMatchedCartridgeSpecId] = useState<string | null>(null)

  const [aiFillQuery, setAiFillQuery] = useState('')
  const [aiFillLoading, setAiFillLoading] = useState(false)
  const [aiStatusMessage, setAiStatusMessage] = useState<string | null>(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [cartridgeSpecs, setCartridgeSpecs] = useState<CartridgeSpecDto[]>([])
  const [loadingSpecs, setLoadingSpecs] = useState(false)

  useEffect(() => {
    const restored = consumeAmmoSubmitFormState()
    if (restored) {
      setActiveStep(restored.activeStep)
      setSpecForm(restored.specForm)
      setAmmoForm(restored.ammoForm)
      setMatchedCartridgeSpecId(restored.matchedCartridgeSpecId ?? null)
    }
  }, [])

  useEffect(() => {
    setLoadingSpecs(true)
    agent.CartridgeSpecs.list({ pageNumber: 1, pageSize: 100 })
      .then((result: PagedResult<CartridgeSpecDto>) => {
        setCartridgeSpecs(result.items)
      })
      .finally(() => setLoadingSpecs(false))
  }, [])

  const matchedSpecLabel = useMemo(() => {
    if (!matchedCartridgeSpecId) return null
    const spec = cartridgeSpecs.find((s) => s.id === matchedCartridgeSpecId)
    return spec?.name ?? null
  }, [matchedCartridgeSpecId, cartridgeSpecs])

  const updateSpecForm = useCallback((patch: Partial<CartridgeSpecFormValues>) => {
    setSpecForm((prev) => {
      const next = { ...prev, ...patch }
      return next
    })
    setMatchedCartridgeSpecId(null)
  }, [])

  const buildSnapshot = useCallback(
    () => ({
      activeStep,
      specForm,
      ammoForm,
      matchedCartridgeSpecId
    }),
    [activeStep, specForm, ammoForm, matchedCartridgeSpecId]
  )

  const persistAndOpenHelp = useCallback(
    (field: AmmoHelpFieldKey, step: AmmoSubmitStep) => {
      const snapshot = { ...buildSnapshot(), activeStep: step }
      saveAmmoSubmitFormState(snapshot)
      history.push(buildAmmoHelpUrl(field, AMMO_SUBMIT_RETURN_PATH, snapshot))
    },
    [buildSnapshot, history]
  )

  const handleAiFill = async () => {
    const query = aiFillQuery.trim()
    if (!query) return
    setAiFillLoading(true)
    try {
      const result = await agent.AiAssist.ammoProductFill(query)
      if (!isAmmoProductFillResultValid(result)) {
        toast.error(AI_FILL_FAILED_MESSAGE)
        setAiStatusMessage(AI_FILL_FAILED_MESSAGE)
        return
      }
      const applied = applyAmmoProductFill(result, specForm, ammoForm)
      setSpecForm(applied.specForm)
      setAmmoForm((prev) => ({ ...prev, ...applied.ammoForm }))
      setMatchedCartridgeSpecId(applied.matchedCartridgeSpecId)
      setActiveStep(1)
      const successMsg = 'Suggestions applied — review before submitting.'
      toast.success(successMsg)
      setAiStatusMessage(successMsg)
    } catch (error) {
      const msg = getAmmoAiFillErrorMessage(error)
      toast.error(msg)
      setAiStatusMessage(msg)
    } finally {
      setAiFillLoading(false)
    }
  }

  const validateStep0 = () => {
    if (!specForm.name?.trim() || !specForm.shortName?.trim() || !specForm.caliber?.trim()) {
      toast.error('Fill in cartridge name, short name, and caliber')
      return false
    }
    return true
  }

  const validateStep1 = () => {
    if (!ammoForm.brand?.trim() || !ammoForm.name?.trim() || ammoForm.grainWeight === undefined || ammoForm.grainWeight === null) {
      toast.error('Fill in manufacturer, product line, and bullet weight')
      return false
    }
    return true
  }

  const handleNext = () => {
    if (!validateStep0()) return
    setActiveStep(1)
  }

  const handleFinalSubmit = async () => {
    if (!validateStep1()) return
    setSubmitLoading(true)
    try {
      let cartridgeSpecId = matchedCartridgeSpecId
      if (!cartridgeSpecId) {
        const spec = await agent.CartridgeSpecs.create(specForm)
        cartridgeSpecId = spec.id
      }
      const result = await agent.AmmoProducts.create({
        ...ammoForm,
        cartridgeSpecId
      })
      toast.success('Ammo product submitted!')
      history.push(`/ammo/products/${result.id}`)
    } catch {
      toast.error('Failed to submit')
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh' }}>
      <Box
        sx={{
          borderBottom: '3px solid var(--gs-ammo-border)',
          marginBottom: '24px'
        }}
      >
        <Container size="md" sx={{ paddingTop: '20px', paddingBottom: '8px' }}>
          <AppButton
            variant="subtle"
            onClick={() => history.push('/ammo')}
            sx={{ width: 'fit-content', paddingLeft: 0, marginBottom: 8 }}
          >
            ← Back to Ammo Registry
          </AppButton>
        </Container>
      </Box>

      <Container size="md" sx={{ paddingBottom: '32px', position: 'relative' }}>
        <AiFillStatusAnnouncer message={aiStatusMessage} />
        <AppStack spacing="lg">
          <Box>
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: '32px',
                fontWeight: 700,
                color: 'var(--gs-text)',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                borderBottom: '3px solid var(--gs-gold)',
                display: 'inline-block',
                paddingBottom: '4px',
                marginBottom: '8px'
              }}
            >
              Submit Ammo
            </AppText>
            <AppText sx={{ color: 'var(--gs-muted)', fontSize: '14px' }}>
              Add to the GunSlop registry. Experts: fill all details.
            </AppText>
          </Box>

          <AppCard sx={{ background: 'var(--gs-surface)' }}>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 12,
                fontWeight: 600,
                color: '#58A6FF',
                marginBottom: 8
              }}
            >
              Fill with AI
            </AppText>
            <Group spacing="sm" sx={standardInputFlexGroupSx} align="flex-end" noWrap>
              <StandardInput
                placeholder="e.g. Federal Gold Medal .308 175gr BTHP"
                value={aiFillQuery}
                onChange={(e) => setAiFillQuery(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAiFill()
                }}
                disabled={aiFillLoading}
              />
              <AppButton
                onClick={handleAiFill}
                loading={aiFillLoading}
                disabled={!aiFillQuery.trim()}
                data-testid="ammo-ai-fill-button"
                sx={{
                  background: 'rgba(88,166,255,0.15)',
                  border: '1px solid rgba(88,166,255,0.3)',
                  color: '#58A6FF',
                  flexShrink: 0,
                  '&:hover': { background: 'rgba(88,166,255,0.25)' }
                }}
              >
                Fill with AI
              </AppButton>
            </Group>
            <AppText
              component="p"
              role="status"
              sx={{ fontSize: 11, color: 'var(--gs-muted)', marginTop: 10 }}
            >
              These values are suggestions. Verify against product labels.
            </AppText>
          </AppCard>

          {aiFillLoading && (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          )}

          <AppCard sx={{ background: 'var(--gs-surface)' }}>
            <Stepper
              active={activeStep}
              allowNextStepsSelect
              onStepClick={(step) => {
                if (step === 0) setActiveStep(0)
                else if (step === 1 && validateStep0()) setActiveStep(1)
              }}
              breakpoint="sm"
            >
              <Stepper.Step label="Define Cartridge" description="Spec details">
                <AppStack spacing="md" mt="md">
                  <AppInput
                    label="Name (required)"
                    placeholder="e.g. 9mm Luger"
                    value={specForm.name}
                    onChange={(e) => updateSpecForm({ name: e.currentTarget.value })}
                  />
                  <AppInput
                    label="Short Name (required)"
                    placeholder="e.g. 9mm"
                    value={specForm.shortName}
                    onChange={(e) => updateSpecForm({ shortName: e.currentTarget.value })}
                  />
                  <Box>
                    <LabelWithHelp
                      label="Caliber"
                      required
                      onHelpClick={() => persistAndOpenHelp('caliber', 0)}
                    />
                    <AppInput
                      placeholder="e.g. 9x19mm"
                      aria-label="Caliber"
                      value={specForm.caliber}
                      onChange={(e) => updateSpecForm({ caliber: e.currentTarget.value })}
                    />
                  </Box>
                  <Box>
                    <LabelWithHelp
                      label="Type"
                      onHelpClick={() => persistAndOpenHelp('type', 0)}
                    />
                    <AppSelect
                      aria-label="Type"
                      data={[...AMMO_TYPES]}
                      value={specForm.type}
                      onChange={(val) => updateSpecForm({ type: val || AMMO_TYPES[0] })}
                    />
                  </Box>
                  <Group position="right" mt="sm">
                    <AppButton onClick={handleNext} data-testid="ammo-step-next">
                      Next
                    </AppButton>
                  </Group>
                </AppStack>
              </Stepper.Step>

              <Stepper.Step label="Add Product" description="Load details">
                <AppStack spacing="md" mt="md">
                  <CartridgePreviewCard
                    specForm={specForm}
                    matchedCartridgeSpecId={matchedCartridgeSpecId}
                    matchedSpecLabel={matchedSpecLabel}
                    onEditCartridge={() => setActiveStep(0)}
                  />

                  <Box>
                    <LabelWithHelp
                      label="Manufacturer"
                      required
                      onHelpClick={() => persistAndOpenHelp('brand', 1)}
                    />
                    <AppInput
                      placeholder="e.g. Federal"
                      aria-label="Manufacturer"
                      data-testid="ammo-submit-brand"
                      value={ammoForm.brand}
                      onChange={(e) => setAmmoForm({ ...ammoForm, brand: e.currentTarget.value })}
                    />
                  </Box>
                  <Box>
                    <LabelWithHelp
                      label="Product Line"
                      required
                      onHelpClick={() => persistAndOpenHelp('name', 1)}
                    />
                    <AppInput
                      placeholder="e.g. Gold Medal"
                      aria-label="Product Line"
                      data-testid="ammo-submit-product-line"
                      value={ammoForm.name}
                      onChange={(e) => setAmmoForm({ ...ammoForm, name: e.currentTarget.value })}
                    />
                  </Box>
                  <Box>
                    <LabelWithHelp
                      label="Bullet Weight gr"
                      required
                      onHelpClick={() => persistAndOpenHelp('grainWeight', 1)}
                    />
                    <AppNumberInput
                      placeholder="e.g. 124"
                      aria-label="Bullet Weight gr"
                      data-testid="ammo-submit-grain-weight"
                      value={ammoForm.grainWeight}
                      onChange={(val) => setAmmoForm({ ...ammoForm, grainWeight: val ?? undefined })}
                    />
                  </Box>
                  <Box>
                    <LabelWithHelp
                      label="Bullet Type"
                      onHelpClick={() => persistAndOpenHelp('bulletType', 1)}
                    />
                    <AppSelect
                      aria-label="Bullet Type"
                      data={[...BULLET_TYPES]}
                      value={ammoForm.bulletType}
                      onChange={(val) => setAmmoForm({ ...ammoForm, bulletType: val || BULLET_TYPES[0] })}
                    />
                  </Box>

                  <Group position="apart" mt="sm">
                    <AppButton variant="light" onClick={() => setActiveStep(0)} data-testid="ammo-step-back">
                      Back
                    </AppButton>
                    <AppButton
                      onClick={handleFinalSubmit}
                      loading={submitLoading}
                      disabled={loadingSpecs}
                      data-testid="ammo-submit-final"
                    >
                      Add to catalog
                    </AppButton>
                  </Group>
                </AppStack>
              </Stepper.Step>
            </Stepper>
          </AppCard>
        </AppStack>
      </Container>
    </Box>
  )
}

export default SubmitAmmoPage
